"use client"

import {useEffect} from "react";
import {useLocale} from "next-intl";
import {useAppSelector} from "@/lib/hooks";
import {
    cioIdentifyUser,
    cioTrack,
    getAnonymousId,
    isCioEnabled,
} from "@/lib/analytics/customerio";
import {safeLocalStorage, safeSessionStorage} from "@/lib/utils/storage";
import {CioEvents} from "@/lib/analytics/events";

/** Fired at most once per browser session for an anonymous visitor. */
const SITE_VISITED_SESSION_KEY = "cio_site_visited";

/**
 * Bootstraps Customer.io identity and emits the anonymous site-visit event.
 * Mounted once in the website layout (AC-613).
 */
export default function CioInitializer() {
    const userId = useAppSelector((state) => state.auth.userId);
    const locale = useLocale();

    // Identify the player once authenticated. cio.js then auto-merges the
    // anonymous activity it tracked under `_cioanonid` into this profile
    // (client-side merge — requires "Anonymous event merging" enabled in the
    // workspace; merges events from the last 30 days).
    //
    // Anonymous visitors are intentionally NOT identified: cio.js has no
    // client-side alias, so identifying by our `anonymous_id` would create a
    // separate profile and defeat the merge. We just let events go out as
    // anonymous (cio.js attaches `_cioanonid` automatically).
    //
    // `language` is the standard Customer.io attribute that drives message
    // translations (campaign language branches, newsletter/transactional
    // translations). It re-syncs whenever the player switches the site locale.
    useEffect(() => {
        if (!isCioEnabled() || !userId) return;
        cioIdentifyUser(userId, {language: locale});
    }, [userId, locale]);

    // `site_visited_without_registration` — once per session, anonymous visitors only.
    useEffect(() => {
        if (!isCioEnabled() || userId || typeof window === "undefined") return;
        if (safeSessionStorage.getItem(SITE_VISITED_SESSION_KEY)) return;
        safeSessionStorage.setItem(SITE_VISITED_SESSION_KEY, "1");

        const params = new URLSearchParams(window.location.search);
        cioTrack(CioEvents.SITE_VISITED_WITHOUT_REGISTRATION, {
            anonymous_id: getAnonymousId(),
            landing_url: window.location.href,
            referrer: document.referrer || undefined,
            utm_source: params.get("utm_source") || undefined,
            utm_medium: params.get("utm_medium") || undefined,
            utm_campaign: params.get("utm_campaign") || undefined,
            // `traffic` query param is persisted by RouteObserver; partner_id is
            // resolved server-side from the traffic URL, so it is omitted here.
            traffic_url_id: safeLocalStorage.getItem("traffic") || undefined,
            created_at: new Date().toISOString(),
        });
    }, [userId]);

    return null;
}
